import { useEffect } from 'react';
import { usePrefersReducedMotion } from '@hooks/usePrefersReducedMotion';

// At module scope: the React Compiler cannot lower an `import()` expression, so one inside the hook
// made it bail out and ship the hook unmemoised. A call to this function compiles.
const loadConfetti = () => import('canvas-confetti');

/**
 * Two-cannon canvas-confetti burst when `active` turns true, skipped under prefers-reduced-motion.
 * A burst already in the air when `active` turns false (or the caller unmounts) finishes and falls
 * past whatever closed, and its canvas is removed when it ends; only reduced motion stops it at
 * once. The confetti chunk is dynamically imported so it only loads when a celebration actually
 * fires; chunk-load failures are swallowed because the burst is purely decorative.
 */
export function useConfettiBurst(active: boolean): void {
  const reducedMotion = usePrefersReducedMotion();
  useEffect(() => {
    if (!active || reducedMotion) return;
    let cancelled = false;
    let teardown: (() => void) | undefined;
    void loadConfetti()
      .then(({ default: confetti }) => {
        if (cancelled) return;
        // Render on the MAIN THREAD via a created instance (`useWorker: false`). The global
        // `confetti()` hardcodes useWorker:true, and its worker body is `main.toString()` — in
        // our minified prod bundle that stringified function closes over a hoisted, renamed
        // identifier that is undefined in the worker's isolated scope (`ReferenceError: t is
        // not defined`), so the worker dies and no confetti renders. Our own canvas + a
        // no-worker instance skips that path entirely.
        const canvas = document.createElement('canvas');
        // Fixed full-screen overlay ABOVE the drawer; uses the overlay-escape layer so it
        // always renders above the drawer's z-overlay stack, never intercepts taps.
        canvas.style.cssText =
          'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:var(--z-index-overlay-escape)';
        document.body.appendChild(canvas);
        // A blocked or lost canvas (privacy modes, jsdom) returns a null 2D context.
        // `confetti.create()` still hands back a callable fire function in that case, but
        // `fire.reset()` reaches for the context internally and throws on unmount. Skip
        // creating the instance entirely when there is no context to draw into.
        if (!canvas.getContext('2d')) {
          teardown = () => {
            canvas.remove();
          };
          return;
        }
        const fire = confetti.create(canvas, { useWorker: false, resize: true });
        const shot = { particleCount: 80, startVelocity: 45, spread: 55, ticks: 200 };
        // fire() resolves when the animation ends (null where Promise is missing); the second shot
        // joins the first one's animation.
        const done = Promise.all([
          fire({ ...shot, angle: 60, origin: { x: 0, y: 0.7 } }),
          fire({ ...shot, angle: 120, origin: { x: 1, y: 0.7 } }),
        ]);
        teardown = () => {
          // A burst already in the air finishes and falls past whatever closed (the canvas has
          // pointer-events: none); wiping it mid-air is the jarring change. Only a reduced-motion
          // preference stops it at once.
          if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            fire.reset();
            canvas.remove();
            return;
          }
          const remove = () => {
            canvas.remove();
          };
          void done.then(remove, remove);
        };
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      teardown?.();
    };
  }, [active, reducedMotion]);
}
